import {
  React,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Formik,
  Form,
  Yup,
  ChevronLeft,
  useNavigate,
  IconButton,
  FormikHelpers,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Alert,
  CircularProgress,
  Sparkles,
  useState,
  useEffect,
  useRef,
} from '../../utils/globalImports';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetClientsByCustomerIdQuery, useGetClientByIdQuery } from '../../api/clientApiSlice';
import { CustomerResponse } from '../../store/types';
import IconSelector from '../../components/selector/IconSelector';
import { useTranslation } from 'react-i18next';
import { useAutoConfigureDataAgentMutation } from '../../api/dataAgentApiSlice';

interface ExtendedAccountResponse {
  role?: string;
  email?: string;
  customer_id?: string;
  client_id?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-agent-tabpanel-${index}`}
      aria-labelledby={`data-agent-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `data-agent-tab-${index}`,
    'aria-controls': `data-agent-tabpanel-${index}`,
  };
}

interface AddEditDataAgentUIFormProps {
  entityType: string;
  initialValues: Record<string, any>;
  dataAgentId?: string;
  validationSchema: Yup.ObjectSchema<any>;
  onSubmit: (values: any, formikHelpers: FormikHelpers<any>) => void;
  onClose?: () => void;
  customerData?: { customer_id: string; name: string };
  customerName?: string;
  customerList?: CustomerResponse[];
  customerLoading?: boolean;
  onAutoConfigureComplete?: () => void;
}

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error.filter((e) => typeof e === 'string').join(', ');
  }
  return '';
};

const AddEditDataAgentUIForm: React.FC<AddEditDataAgentUIFormProps> = ({
  entityType,
  initialValues,
  validationSchema,
  onSubmit,
  onClose,
  dataAgentId,
  customerData: propCustomerData,
  customerName: propCustomerName,
  customerList = [],
  customerLoading = false,
  onAutoConfigureComplete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // ── Auto-configure state ─────────────────────────────────────────────────
  const [isAutoConfiguring, setIsAutoConfiguring] = useState(false);
  const [autoConfigureSteps, setAutoConfigureSteps] = useState<{ step: number; message: string; status: string }[]>([]);
  const [autoConfigureError, setAutoConfigureError] = useState<string | null>(null);
  const [autoConfigureDone, setAutoConfigureDone] = useState(false);
  const [autoCfgPanelOpen, setAutoCfgPanelOpen] = useState(false);
  const autoConfigureAbortRef = useRef<AbortController | null>(null);

  // Get auth state and customer ID for API headers
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const customerId = account?.customer_id;
  const [autoConfigureDataAgent] = useAutoConfigureDataAgentMutation();

  const handleAutoConfig = async (setFieldValue: (f: string, v: any) => void) => {
    if (!dataAgentId || !selectedCustomerId || !selectedClientId) return;

    setIsAutoConfiguring(true);
    setAutoConfigureSteps([]);
    setAutoConfigureError(null);
    setAutoConfigureDone(false);
    setAutoCfgPanelOpen(true);

    const controller = new AbortController();
    autoConfigureAbortRef.current = controller;

    try {
      const stream = await autoConfigureDataAgent({
        data_agent_id: dataAgentId,
        customer_id: selectedCustomerId,
        client_id: selectedClientId,
      }).unwrap();

      const reader = (stream as unknown as ReadableStream).getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.replace(/^data:\s*/, '').trim();
          if (!line) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'status') {
              setAutoConfigureSteps((prev) => {
                const existing = prev.findIndex((s) => s.step === event.step);
                const entry = { step: event.step, message: event.message, status: event.status ?? 'in-progress' };
                if (existing >= 0) {
                  const updated = [...prev];
                  updated[existing] = entry;
                  return updated;
                }
                return [...prev, entry];
              });
            } else if (event.type === 'result' && event.done) {
              setAutoConfigureDone(true);
              setTimeout(() => onAutoConfigureComplete?.(), 1500);
            } else if (event.type === 'error') {
              setAutoConfigureError(event.message ?? 'Auto-configure failed.');
            }
          } catch {
            // ignore non-JSON lines
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setAutoConfigureError(err.message ?? 'Auto-configure failed.');
      }
    } finally {
      setIsAutoConfiguring(false);
      autoConfigureAbortRef.current = null;
    }
  };
  // Get user role from Redux (auth state already retrieved above)
  const userRole = (account?.role || '').trim().toLowerCase();
  const { selectedCustomerId, selectedClientId } = useSelector((state: RootState) => state.vaultilake);
  const shouldHideCustomerClientFields = selectedCustomerId && selectedClientId;
  const clientId = account?.client_id;

  // Determine if fields should be disabled based on role
  const isCustomerIdDisabled = userRole === 'customeradmin' || userRole === 'clientadmin';
  const isClientIdDisabled = userRole === 'clientadmin' || entityType === 'edit';

  const customerData =
    propCustomerData ||
    (userRole === 'customeradmin' || userRole === 'clientadmin'
      ? {
          customer_id: account?.customer_id || '',
          name: propCustomerName || account?.customer_id || '',
        }
      : undefined);

  // Fetch client data for clientadmin
  const { data: clientData, isLoading: clientLoading } = useGetClientByIdQuery(
    { clientId: clientId },
    {
      skip: userRole !== 'clientadmin' || !clientId,
    },
  );

  // Check if we're in edit mode
  const isEditMode = entityType === 'edit';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Reset to first tab if switching from edit to add mode
  useEffect(() => {
    if (!isEditMode && tabValue > 0) {
      setTabValue(0);
    }
  }, [isEditMode, tabValue]);

  // Sort customer list alphabetically
  const sortedCustomerList = [...customerList].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  // Customer and Client Fields Component (to avoid duplication)
  const CustomerClientFields = ({ values, handleChange, handleBlur, errors, touched, setFieldValue }: any) => {
    // Fetch clients based on selected customer
    const { data: dynamicClientList = [], isLoading: dynamicClientLoading } = useGetClientsByCustomerIdQuery(
      values.customer_id,
      {
        skip: !values.customer_id || userRole === 'clientadmin',
      },
    );

    const sortedFilteredClientList = [...dynamicClientList].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );

    const handleCustomerChange = (event: SelectChangeEvent<string>) => {
      const newCustomerId = event.target.value as string;
      setFieldValue('customer_id', newCustomerId);
      if (newCustomerId !== values.customer_id) {
        setFieldValue('client_id', '');
      }
    };

    return (
      <>
        {/* Customer Name Field */}
        {userRole !== 'clientadmin' && (
          <Grid size={6}>
            <Typography component="p" sx={{ mb: 2 }}>
              {t('dataAgents.form.customerName')}
              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                *
              </Box>
            </Typography>
            {isCustomerIdDisabled || customerData ? (
              <>
                <TextField
                  value={propCustomerName || customerData?.name || ''}
                  disabled
                  fullWidth
                  size="small"
                  placeholder={t('dataAgents.form.customerNamePlaceholder')}
                  error={Boolean(touched.customer_id && errors.customer_id)}
                  helperText={touched.customer_id && errors.customer_id ? getErrorMessage(errors.customer_id) : ''}
                />
                <input type="hidden" name="customer_id" value={customerData?.customer_id || values.customer_id || ''} />
              </>
            ) : (
              <Select
                name="customer_id"
                value={values.customer_id}
                onChange={handleCustomerChange}
                onBlur={handleBlur}
                fullWidth
                size="small"
                displayEmpty
                disabled={customerLoading}
                error={Boolean(touched.customer_id && errors.customer_id)}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 400, zIndex: 1300 } },
                  disablePortal: false,
                }}
              >
                <MenuItem value="">{t('dataAgents.form.selectCustomer')}</MenuItem>
                {sortedCustomerList.map((customer) => (
                  <MenuItem key={customer.customer_id} value={customer.customer_id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {touched.customer_id && errors.customer_id && !isCustomerIdDisabled && !customerData && (
              <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                {getErrorMessage(errors.customer_id)}
              </Typography>
            )}
          </Grid>
        )}

        {/* Client Name Field */}
        <Grid size={userRole === 'clientadmin' ? 12 : 6}>
          <Typography component="p" sx={{ mb: 2 }}>
            {t('dataAgents.form.clientName')}
            <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
              *
            </Box>
          </Typography>
          {isClientIdDisabled ? (
            <>
              <TextField
                value={
                  userRole === 'clientadmin'
                    ? clientData?.name || ''
                    : entityType === 'edit' && values.client_id
                      ? sortedFilteredClientList.find((client) => client.client_id === values.client_id)?.name || ''
                      : ''
                }
                disabled
                fullWidth
                size="small"
                placeholder={clientLoading ? t('common.loading') : t('dataAgents.form.clientNamePlaceholder')}
                error={Boolean(touched.client_id && errors.client_id)}
                helperText={touched.client_id && errors.client_id ? getErrorMessage(errors.client_id) : ''}
              />
              <input type="hidden" name="client_id" value={values.client_id || ''} />
            </>
          ) : (
            <Select
              name="client_id"
              value={values.client_id}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              size="small"
              displayEmpty
              error={Boolean(touched.client_id && errors.client_id)}
              disabled={dynamicClientLoading || !values.customer_id}
              MenuProps={{
                PaperProps: { style: { maxHeight: 400, zIndex: 1300 } },
                disablePortal: false,
              }}
            >
              <MenuItem value="">{t('dataAgents.form.selectClient')}</MenuItem>
              {sortedFilteredClientList.map((client) => (
                <MenuItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {touched.client_id && errors.client_id && !isClientIdDisabled && (
            <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
              {getErrorMessage(errors.client_id)}
            </Typography>
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      {/* Main Form Container */}
      <Box sx={{ color: '#475569', width: '100%', p: 1, px: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values, formikHelpers) => {
            onSubmit(values, formikHelpers);
          }}
        >
          {({ values, handleChange, handleBlur, errors, touched, isSubmitting, setFieldValue }) => {
            return (
              <>
                <Form noValidate>
                  <Box
                    sx={{
                      py: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                      <IconButton disabled={isSubmitting} aria-label={t('common.back')} onClick={() => navigate(-1)}>
                        <ChevronLeft />
                      </IconButton>
                      {entityType === 'edit' ? t('dataAgents.form.editTitle') : t('dataAgents.form.addTitle')}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      {isEditMode && (
                        <Button
                          disabled={isSubmitting || isAutoConfiguring}
                          variant="outlined"
                          onClick={() => handleAutoConfig(setFieldValue)}
                          startIcon={
                            isAutoConfiguring ? (
                              <CircularProgress size={16} sx={{ color: '#695ea8' }} />
                            ) : (
                              <Sparkles size={16} />
                            )
                          }
                          sx={{
                            height: '40px',
                            textTransform: 'none',
                            borderColor: '#695ea8',
                            color: '#695ea8',
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#5a4f9e',
                              backgroundColor: 'rgba(105, 94, 168, 0.06)',
                            },
                            '&.Mui-disabled': {
                              borderColor: 'rgba(105, 94, 168, 0.3)',
                              color: 'rgba(105, 94, 168, 0.4)',
                            },
                          }}
                        >
                          {isAutoConfiguring ? 'Configuring…' : 'Auto-Configure'}
                        </Button>
                      )}
                      <Button
                        disabled={isSubmitting}
                        variant="contained"
                        color="primary"
                        type="submit"
                        sx={{ width: '180px', height: '40px', textTransform: 'none' }}
                      >
                        {entityType === 'edit' ? t('dataAgents.form.updateButton') : t('dataAgents.form.saveButton')}
                      </Button>
                    </Box>
                  </Box>

                  {/* ── Auto-Configure Progress Panel ────────────────────── */}
                  {isEditMode && autoCfgPanelOpen && (
                    <Collapse in={autoCfgPanelOpen}>
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 2,
                          mb: 1,
                          p: 2,
                          border: '1px solid rgba(105, 94, 168, 0.25)',
                          borderRadius: 2,
                          background: 'rgba(105, 94, 168, 0.04)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#695ea8',
                              fontFamily: 'Poppins, sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Sparkles size={14} />
                            AI Auto-Configure
                          </Typography>
                          {!isAutoConfiguring && (
                            <Button
                              size="small"
                              sx={{ textTransform: 'none', color: '#888', fontSize: '0.7rem' }}
                              onClick={() => setAutoCfgPanelOpen(false)}
                            >
                              Dismiss
                            </Button>
                          )}
                        </Box>

                        {isAutoConfiguring && (
                          <LinearProgress
                            sx={{
                              mb: 1.5,
                              borderRadius: 4,
                              height: 4,
                              backgroundColor: 'rgba(105, 94, 168, 0.15)',
                              '& .MuiLinearProgress-bar': { backgroundColor: '#695ea8' },
                            }}
                          />
                        )}

                        {autoConfigureError && (
                          <Alert severity="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
                            {autoConfigureError}
                          </Alert>
                        )}

                        {autoConfigureDone && (
                          <Alert severity="success" sx={{ mb: 1, fontSize: '0.8rem' }}>
                            Auto-configure complete — reloading form with new configuration…
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {autoConfigureSteps.map((s) => (
                            <Box key={s.step} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {s.status === 'completed' ? (
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: '#695ea8',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>✓</Typography>
                                </Box>
                              ) : (
                                <CircularProgress size={14} sx={{ color: '#695ea8', flexShrink: 0 }} />
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: s.status === 'completed' ? '#444' : '#695ea8',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: s.status === 'completed' ? 400 : 500,
                                }}
                              >
                                {s.message}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Collapse>
                  )}

                  {/* Conditional Tabs - Only show tabs when in edit mode, otherwise show content directly */}
                  {isEditMode ? (
                    <>
                      {/* Master Tab */}
                      <TabPanel value={tabValue} index={0}>
                        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                          {/* Data Agent Name */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.name')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.namePlaceholder')}
                              error={Boolean(touched.name && errors.name)}
                              helperText={touched.name && errors.name ? getErrorMessage(errors.name) : ''}
                            />
                          </Grid>

                          {/* Customer and Client Fields - NOW BELOW Icon and Name */}
                          {(userRole === 'superadmin' || userRole === 'customeradmin') &&
                            !shouldHideCustomerClientFields && (
                              <CustomerClientFields
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                setFieldValue={setFieldValue}
                              />
                            )}

                          {/* Description */}
                          <Grid size={12}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.description')}
                            </Typography>
                            <TextField
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.descriptionPlaceholder')}
                              multiline
                              rows={3}
                              error={Boolean(touched.description && errors.description)}
                              helperText={
                                touched.description && errors.description ? getErrorMessage(errors.description) : ''
                              }
                            />
                          </Grid>

                          {/* Instructions */}
                          <Grid size={12}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.instructions')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="instructions"
                              value={values.instructions}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.instructionsPlaceholder')}
                              multiline
                              rows={4}
                              error={Boolean(touched.instructions && errors.instructions)}
                              helperText={
                                touched.instructions && errors.instructions ? getErrorMessage(errors.instructions) : ''
                              }
                            />
                          </Grid>

                          {/* System Prompt */}
                          <Grid size={12}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.systemPrompt')}
                            </Typography>
                            <TextField
                              name="system_prompt"
                              value={values.system_prompt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.systemPromptPlaceholder')}
                              multiline
                              rows={4}
                            />
                          </Grid>

                          {/* Max Tokens */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.maxTokens')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="max_tokens"
                              type="number"
                              value={values.max_tokens}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.maxTokensPlaceholder')}
                              error={Boolean(touched.max_tokens && errors.max_tokens)}
                              helperText={
                                touched.max_tokens && errors.max_tokens ? getErrorMessage(errors.max_tokens) : ''
                              }
                              inputProps={{ min: 1, max: 128000 }}
                            />
                          </Grid>

                          {/* Temperature */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.temperature')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="temperature"
                              type="number"
                              value={values.temperature}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.temperaturePlaceholder')}
                              error={Boolean(touched.temperature && errors.temperature)}
                              helperText={
                                touched.temperature && errors.temperature ? getErrorMessage(errors.temperature) : ''
                              }
                              inputProps={{ min: 0, max: 2, step: 0.1 }}
                            />
                          </Grid>

                          {/* Is Active Toggle */}
                          <Grid size={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="is_active"
                                  checked={values.is_active}
                                  onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('dataAgents.form.isActive')}
                            />
                          </Grid>

                          {/* Is Published Toggle */}
                          <Grid size={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="is_published"
                                  checked={values.is_published}
                                  onChange={(e) => setFieldValue('is_published', e.target.checked)}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('dataAgents.form.isPublished')}
                            />
                          </Grid>
                        </Grid>
                      </TabPanel>
                    </>
                  ) : (
                    /* Add Mode - Show form directly without tabs */
                    <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      {/* Data Agent Name */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.name')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.namePlaceholder')}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name ? getErrorMessage(errors.name) : ''}
                        />
                      </Grid>

                      {/* Customer and Client Fields - NOW BELOW Icon and Name */}
                      {(userRole === 'superadmin' || userRole === 'customeradmin') &&
                        !shouldHideCustomerClientFields && (
                          <CustomerClientFields
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                          />
                        )}

                      {/* Description */}
                      <Grid size={12}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.description')}
                        </Typography>
                        <TextField
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.descriptionPlaceholder')}
                          multiline
                          rows={3}
                          error={Boolean(touched.description && errors.description)}
                          helperText={
                            touched.description && errors.description ? getErrorMessage(errors.description) : ''
                          }
                        />
                      </Grid>

                      {/* Instructions */}
                      <Grid size={12}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.instructions')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="instructions"
                          value={values.instructions}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.instructionsPlaceholder')}
                          multiline
                          rows={4}
                          error={Boolean(touched.instructions && errors.instructions)}
                          helperText={
                            touched.instructions && errors.instructions ? getErrorMessage(errors.instructions) : ''
                          }
                        />
                      </Grid>

                      {/* System Prompt */}
                      <Grid size={12}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.systemPrompt')}
                        </Typography>
                        <TextField
                          name="system_prompt"
                          value={values.system_prompt}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.systemPromptPlaceholder')}
                          multiline
                          rows={4}
                        />
                      </Grid>

                      {/* Max Tokens */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.maxTokens')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="max_tokens"
                          type="number"
                          value={values.max_tokens}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.maxTokensPlaceholder')}
                          error={Boolean(touched.max_tokens && errors.max_tokens)}
                          helperText={touched.max_tokens && errors.max_tokens ? getErrorMessage(errors.max_tokens) : ''}
                          inputProps={{ min: 1, max: 128000 }}
                        />
                      </Grid>

                      {/* Temperature */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.temperature')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="temperature"
                          type="number"
                          value={values.temperature}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.temperaturePlaceholder')}
                          error={Boolean(touched.temperature && errors.temperature)}
                          helperText={
                            touched.temperature && errors.temperature ? getErrorMessage(errors.temperature) : ''
                          }
                          inputProps={{ min: 0, max: 2, step: 0.1 }}
                        />
                      </Grid>

                      {/* Is Active Toggle */}
                      <Grid size={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="is_active"
                              checked={values.is_active}
                              onChange={(e) => setFieldValue('is_active', e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label={t('dataAgents.form.isActive')}
                        />
                      </Grid>

                      {/* Is Published Toggle */}
                      <Grid size={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="is_published"
                              checked={values.is_published}
                              onChange={(e) => setFieldValue('is_published', e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label={t('dataAgents.form.isPublished')}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Form>
              </>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default AddEditDataAgentUIForm;
